import { AppShell, Box, NavLink, Stack } from '@mantine/core';
import { Link, Outlet, createFileRoute, useMatchRoute } from '@tanstack/react-router';

import { IconApi, IconCreditCard, IconDashboard, IconFolder, IconUsers } from '@tabler/icons-react';
import { z } from 'zod';
import { ExternalNavLinks } from '@/components/ExternalNavLinks';
import { NavbarBrand } from '@/components/NavbarBrand';
import { isPersonalTenant } from '@/lib/auth';

export const Route = createFileRoute('/_main/_project')({
  validateSearch: z.object({ project: z.string().optional() }),
  component: Component,
});

function Component() {
  return (
    <>
      <AppShell.Navbar p="md">
        <Stack gap="0" justify="space-between" h="100%">
          <Box>
            <NavbarBrand />
            <NavbarContents />
          </Box>
          <ExternalNavLinks />
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <Box mih="calc(100dvh - 47px)">
          <Outlet />
        </Box>
      </AppShell.Main>
    </>
  );
}

export default function NavbarContents() {
  const { project } = Route.useSearch();
  const matchRoute = useMatchRoute();
  const { userInfo } = Route.useRouteContext();
  // A personal (free) tenant has a single project of its own and nobody to share
  // it with, so the project framing and its member/settings pages are left out.
  const isPersonal = isPersonalTenant(userInfo);
  const iconProps = {
    size: '1.4em',
    stroke: 1.5,
  };
  return (
    <>
      {!isPersonal && (
        <AppShell.Section>
          <div className="text-xs m-3 font-bold text-plamo-text-secondary">プロジェクト</div>
        </AppShell.Section>
      )}
      {/* 無料プランのキーは個人 API キー 1 本だけなので、発行の場をプロファイルに寄せる */}
      {isPersonal ? (
        <NavLink
          component={Link}
          to="/profile"
          active={!!matchRoute({ to: '/profile' })}
          label="API キー"
          leftSection={<IconApi {...iconProps} />}
        />
      ) : (
        <NavLink
          component={Link}
          to="/project/api-keys"
          search={{ project }}
          active={!!matchRoute({ to: '/project/api-keys' })}
          label="API キー"
          leftSection={<IconApi {...iconProps} />}
        />
      )}
      {!isPersonal && (
        <NavLink
          component={Link}
          to="/project/members"
          search={{ project }}
          active={!!matchRoute({ to: '/project/members' })}
          label="メンバー"
          leftSection={<IconUsers {...iconProps} />}
        />
      )}
      <NavLink
        component={Link}
        to="/project/usage"
        search={{ project }}
        active={!!matchRoute({ to: '/project/usage' })}
        label="利用状況"
        leftSection={<IconDashboard {...iconProps} />}
      />
      {!isPersonal && (
        <NavLink
          component={Link}
          to="/project/settings"
          search={{ project }}
          active={!!matchRoute({ to: '/project/settings' })}
          label="プロジェクト情報"
          leftSection={<IconFolder {...iconProps} />}
        />
      )}
      {isPersonal && (
        <NavLink
          component={Link}
          to="/upgrade"
          active={!!matchRoute({ to: '/upgrade' })}
          label="有料プランに申し込む"
          leftSection={<IconCreditCard {...iconProps} />}
        />
      )}
    </>
  );
}
